import { useContext } from 'react';

import { AppStateContext } from './AppStateContext.js';
import {
  appScopedMutateFns,
  useAppStateInternal,
  userScopedMutateFns,
  type _UseAppStateResult as UseAppStateResult,
} from './useAppStateInternal.js';

/**
 * Reads and writes an app-scoped state key (shared across all users).
 * @internal
 */
export function useAppState<T = unknown>(key: string): UseAppStateResult<T> {
  return useAppStateInternal<T>(
    key,
    appScopedMutateFns,
    useContext(AppStateContext),
  );
}

/**
 * Reads and writes a user-scoped app state key.
 *
 * Works without a provider — uses the module-level default context with
 * `scope: 'user'`. Add an `<AppStateProvider>` to get the initial bulk
 * fetch and provider-isolated store.
 *
 * Set `enabled` to `false` to suppress all fetches for this key (analogous to
 * React Query's `enabled` option). The hook still satisfies the rules of hooks
 * and returns stable no-op defaults.
 * @internal
 */
export function useUserAppState<T = unknown>(
  key: string,
  enabled: boolean = true,
): UseAppStateResult<T> {
  return useAppStateInternal<T>(
    key,
    userScopedMutateFns,
    useContext(AppStateContext),
    enabled,
  );
}
